/**
 * usePushNotifications — Capacitor Push Notifications integration
 *
 * Handles:
 *   1. Requesting push notification permission on native iOS
 *   2. Registering device token with the ClariMe backend
 *   3. Routing notification taps to the correct screen
 *   4. Handling token refresh
 *
 * Uses static import for the Capacitor plugin (same pattern as biometricService.js).
 * Graceful no-op when running in browser or plugin unavailable.
 *
 * Install:
 *   npm install @capacitor/push-notifications
 *   npx cap sync ios
 */

import { useEffect, useRef, useCallback } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { registerPushToken, unregisterPushToken } from '../services/apiService';

const PUSH_TOKEN_KEY = 'clarime_push_token';

/**
 * Check if we're in a native Capacitor environment
 */
function isNative() {
  return !!window.Capacitor?.isNativePlatform?.();
}

/**
 * Get the PushNotifications plugin (if available)
 */
function getPlugin() {
  if (!isNative()) return null;
  return PushNotifications ?? null;
}

/**
 * usePushNotifications hook
 *
 * @param {object} options
 * @param {function} options.onNotificationTap - Called when user taps a notification.
 *   Receives { type, groupId, visitId } from notification data payload.
 * @returns {object} { registered: boolean }
 */
export function usePushNotifications({ onNotificationTap } = {}) {
  const registeredRef = useRef(false);
  const listenersAddedRef = useRef(false);
  const onNotificationTapRef = useRef(onNotificationTap);

  // Keep the callback ref current without re-running effects
  useEffect(() => {
    onNotificationTapRef.current = onNotificationTap;
  }, [onNotificationTap]);

  /**
   * Register the device token with the backend
   */
  const sendTokenToBackend = useCallback(async (token) => {
    try {
      await registerPushToken(token, 'ios');
      localStorage.setItem(PUSH_TOKEN_KEY, token);
      console.log('[ClariMe Push] Token registered with backend');
    } catch (err) {
      console.warn('[ClariMe Push] Failed to register token:', err.message);
    }
  }, []);

  /**
   * Initialize push notifications: request permission, register, set up listeners
   */
  const init = useCallback(async () => {
    const plugin = getPlugin();
    if (!plugin) {
      console.log('[ClariMe Push] Not native platform, skipping push init');
      return;
    }

    if (registeredRef.current) return;

    try {
      // Check current permission status
      const permStatus = await plugin.checkPermissions();
      console.log('[ClariMe Push] Permission status:', permStatus.receive);

      if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
        // Request permission — iOS shows the system prompt
        const result = await plugin.requestPermissions();
        console.log('[ClariMe Push] Permission result:', result.receive);
        if (result.receive !== 'granted') {
          console.log('[ClariMe Push] Permission denied by user');
          return;
        }
      } else if (permStatus.receive !== 'granted') {
        console.log('[ClariMe Push] Permission not granted:', permStatus.receive);
        return;
      }

      // Set up listeners (only once)
      if (!listenersAddedRef.current) {
        listenersAddedRef.current = true;

        // Called when registration succeeds — we get the device token
        await plugin.addListener('registration', (tokenData) => {
          console.log('[ClariMe Push] Device token received');
          sendTokenToBackend(tokenData.value);
        });

        // Called when registration fails
        await plugin.addListener('registrationError', (error) => {
          console.warn('[ClariMe Push] Registration error:', error.error);
        });

        // Called when a notification is received while app is in foreground
        await plugin.addListener('pushNotificationReceived', (notification) => {
          console.log('[ClariMe Push] Foreground notification:', notification.title);
          // On iOS, foreground notifications are silently received by default.
          // The Capacitor plugin presents them as banners if configured.
          // We don't need to do anything here — the banner shows automatically.
        });

        // Called when the user taps a notification (app was in background or killed)
        await plugin.addListener('pushNotificationActionPerformed', (action) => {
          console.log('[ClariMe Push] Notification tapped:', action.notification?.data);
          const data = action.notification?.data;
          if (data && onNotificationTapRef.current) {
            onNotificationTapRef.current({
              type: data.type,       // 'share' | 'message'
              groupId: data.groupId,
              visitId: data.visitId,
            });
          }
        });
      }

      // Register with APNs — triggers 'registration' listener with the token
      await plugin.register();
      registeredRef.current = true;
      console.log('[ClariMe Push] Registered for remote notifications');
    } catch (err) {
      console.warn('[ClariMe Push] Init failed:', err.message);
    }
  }, [sendTokenToBackend]);

  /**
   * Unregister push token (call on sign-out)
   */
  const unregister = useCallback(async () => {
    const storedToken = localStorage.getItem(PUSH_TOKEN_KEY);
    if (storedToken) {
      try {
        await unregisterPushToken(storedToken);
        console.log('[ClariMe Push] Token unregistered from backend');
      } catch (err) {
        console.warn('[ClariMe Push] Failed to unregister token:', err.message);
      }
      localStorage.removeItem(PUSH_TOKEN_KEY);
    }
    registeredRef.current = false;
  }, []);

  return { init, unregister };
}
